import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Card, Col, Row, Spinner } from 'react-bootstrap';
import OrderObj from './orderObj';
import ChangeDeliveryDate from './ChangeDeliveryDate';
import OrderAgainButton from './OrderAgainButton';
import RefundOrder from './RefundOrder';
import { loadOrders, selectOrders, selectOrdersPagesLoaded, selectOrdersTotalPages } from './ordersSlice';
import { useState } from 'react';
import { selectIsMobileRoute, setIsLoadMore } from '../mobile/mobileSlice';
import { selectToken } from '../user/userSlice';
import { selectOrderPlannerOrders } from '../../pages/AutoshipPages/core/orderPlannerOrdersSlice';
import isToday from 'date-fns/isToday';
import { selectProducts } from '../products/productsSlice';

export default function MyOrdersMobile() {
  const dispatch = useDispatch();
  const token = useSelector(selectToken);
  const products = useSelector(selectProducts);
  const AppURL = useSelector(selectIsMobileRoute);
  const urlParams = AppURL ? `/?${AppURL}` : '';
  const allOrders: Array<Record<string, any>> = useSelector(selectOrders);
  const total_pages: number = useSelector(selectOrdersTotalPages);
  const pages_loaded: number = useSelector(selectOrdersPagesLoaded);
  const [isLoading, setIsLoading] = useState(false);
  const orderPlannerOrders: Array<Record<string, any>> = useSelector(selectOrderPlannerOrders);
  const combinedOrders = [
    ...orderPlannerOrders,
    ...allOrders.filter(
      (order) => !orderPlannerOrders.find((plannerOrder) => plannerOrder.id === order.id)
    ),
  ];
  const sortedOrders = [...combinedOrders].sort((a, b) => {
    const orderA = new OrderObj(a);
    const orderB = new OrderObj(b);
    const aIsProcessingToday = a.status === 'processing' && isToday(new Date(orderA.getDeliveryDate()));
    const bIsProcessingToday = b.status === 'processing' && isToday(new Date(orderB.getDeliveryDate()));

    if (aIsProcessingToday && !bIsProcessingToday) return -1;
    if (!aIsProcessingToday && bIsProcessingToday) return 1;

    return new Date(b.date_modified_gmt).getTime() - new Date(a.date_modified_gmt).getTime();
  });

  const loadMoreOrders = async () => {
    setIsLoading(true);
    dispatch(setIsLoadMore(true));
    if (token && (!pages_loaded || pages_loaded < total_pages)) {
      await dispatch(loadOrders(token));
      await dispatch(setIsLoadMore(false));
      setIsLoading(false);
    }
  }

  return (
    <div className='my-orders-mobile my-5'>
      {sortedOrders.length <= 0
        ? <Card body className='my-1'>
          <Row className='my-1'>
            <Col xs={6} className='fw-bold'>Order</Col>
            <Col xs={6}>
              ---
            </Col>
          </Row>
          <Row className='my-1'>
            <Col xs={6} className='fw-bold'>Date</Col>
            <Col xs={6}>
              ---
            </Col>
          </Row>
          <Row className='my-1'>
            <Col xs={6} className='fw-bold'>Status</Col>
            <Col xs={6}>---</Col>
          </Row>
          <Row className='my-1'>
            <Col xs={6} className='fw-bold'>Delivery Date</Col>
            <Col xs={6}>---</Col>
          </Row>
          <Row className='my-1'>
            <Col xs={6} className='fw-bold'>Total</Col>
            <Col xs={6}>
              ---
            </Col>
          </Row>
          <Row className='my-1'>
            <Col xs={6} className='my-1 fw-bold'>Actions</Col>
            <Col xs={6} className='vstack gap-1'>
              <Button
                as={Link as any}
                to={`/order${urlParams}`}
                variant="link"
                className='text-start text-black p-0'
              >Order</Button>
            </Col>
          </Row>
        </Card>
        : <>
          {sortedOrders.map((orderData, index: number) => {
            const order = new OrderObj(orderData);
            const isGiftCardOrder = OrderObj.hasProductWithSlug(order.data, products, 'gift-card');
            const isMightyBucksCardOrder = OrderObj.hasProductWithSlug(order.data, products, 'mighty-bucks-gift-card-for');
            const isGiftItemOrder = isGiftCardOrder || isMightyBucksCardOrder;
            const isNotGiftItemOrder = !isGiftItemOrder;
            const isAutoshipOrder = order.data.status === 'autoship' ||
              order.data.status === 'mmfailpayment' ||
              order.data.status === 'mmcancelled' ||
              order.data.status === 'autoship-onhold';
            const OrderStatus = order.data.status === 'autoship' ? 'Order Planner Pending' :
              order.data.status === 'mmfailpayment' ? 'Order Planner Failed' :
                order.data.status === 'mmcancelled' ? 'Order Planner Cancelled' :
                  order.data.status === 'autoship-onhold' ? 'Order Planner On hold' : order.data.status;
            const redirectLink = isAutoshipOrder ? `/autoship/orders/${order.data.id}${urlParams}` :
              `/my-account/orders/${order.data.id}${urlParams}`;

            return (
              <Card body className='my-1' key={order.data.id}>
                <Row className='my-1'>
                  <Col xs={6} className='fw-bold'>Order</Col>
                  <Col xs={6}>
                    <Button
                      as={Link as any}
                      to={redirectLink}
                      variant="link"
                      order={order}
                      className='text-black p-0'
                    >#{order.data.id}</Button>
                  </Col>
                </Row>
                <Row className='my-1'>
                  <Col xs={6} className='fw-bold'>Date</Col>
                  <Col xs={6}>
                    {order.getDate()}
                  </Col>
                </Row>
                <Row className='my-1'>
                  <Col xs={6} className='fw-bold'>Status</Col>
                  <Col xs={6}>{OrderStatus}</Col>
                </Row>
                <Row className='my-1'>
                  <Col xs={6} className='fw-bold'>Delivery Date</Col>
                  <Col xs={6}>{isGiftItemOrder ? 'Email Delivery' : order.getDeliveryDate()}</Col>
                </Row>
                <Row className='my-1'>
                  <Col xs={6} className='fw-bold'>Total</Col>
                  <Col xs={6}>
                    <span className='fw-bold'>${order.data.total}</span> for&nbsp;
                    {order.getTotalItems()} items
                  </Col>
                </Row>
                <Row className='my-1'>
                  <Col xs={6} className='my-1 fw-bold'>Actions</Col>
                  <Col xs={6} className='vstack gap-1'>
                    {isAutoshipOrder ?
                      <Link to={redirectLink}>
                        <Button
                          size="sm"
                          variant="success"
                          className='text-white mt-0 my-2 w-100'
                        >
                          View
                        </Button>
                      </Link> :
                      <>
                        <OrderAgainButton order={order} />
                        {isNotGiftItemOrder && order.isModifiable() && <ChangeDeliveryDate order={order} />}
                        {order.isRefundable() && <RefundOrder order={order} />}
                      </>}
                  </Col>
                </Row>
              </Card>

            )
          })}
        </>}
      <div className='d-flex justify-content-center align-items-center'>
        {pages_loaded < total_pages &&
          <Button
            onClick={loadMoreOrders}
            disabled={isLoading}
            variant='success'
            className='text-white'
          >
            {isLoading ?
              <>
                Loading ... &nbsp;
                <Spinner
                  animation="border"
                  as="span"
                  size="sm"
                />
              </>
              : 'Load more'}
          </Button>
        }
      </div>
    </div >
  );
}